"use client";

import React from "react";
import { MapPin, Clock, PhoneCall, CircleCheck as CheckCircle2, ShieldCheck, Navigation, Wrench, Truck, ArrowRight } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const coverageAreas = [{
  name: "Downtown Jonesboro & Historic District",
  zip: "30236",
  note: "Average response: Under 30 mins"
}, {
  name: "Tara Boulevard Corridor",
  zip: "30236 / 30238",
  note: "Rapid commercial & residential dispatch"
}, {
  name: "Lake Spivey & Surrounding Communities",
  zip: "30236",
  note: "Full emergency & water heater crews"
}, {
  name: "Fayetteville Road / GA-54",
  zip: "30238",
  note: "Immediate leak & line repair teams"
}, {
  name: "Mundy's Mill & South Clayton County",
  zip: "30238",
  note: "24/7 on-call master plumbers"
}, {
  name: "Irondale & Flint River Road Area",
  zip: "30238",
  note: "Sewer backup & main line assistance"
}];
const serviceHighlights = [{
  title: "Burst Pipe & Flooding Control",
  description: "Fast water shutoff and pipe repair to stop home damage before it spreads."
}, {
  title: "Emergency Drain & Sewer Clearing",
  description: "Heavy-duty hydro-jetting and motorized snaking for severe backups and overflows."
}, {
  title: "Same-Day Water Heater Repair",
  description: "Diagnosing leaks, faulty elements, and emergency unit replacements across Clayton County."
}, {
  title: "Slab Leaks & Underground Lines",
  description: "Non-destructive acoustic leak detection and targeted line replacements."
}];
const dispatchGuarantees = [{
  icon: Clock,
  label: "24/7 Rapid Response",
  subtext: "Live dispatchers ready around the clock"
}, {
  icon: Truck,
  label: "Fully Equipped Vans",
  subtext: "Most repairs completed on the first visit"
}, {
  icon: ShieldCheck,
  label: "Licensed & Insured",
  subtext: "Georgia certified master plumbing crew"
}, {
  icon: Wrench,
  label: "Upfront Pricing",
  subtext: "Clear estimates before any work begins"
}];
export default function ServiceArea() {
  const shouldReduceMotion = useReducedMotion();
  const fadeInVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="serviceareajonesboro" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        
        {/* Header Block */}
        <motion.div className="max-w-3xl mx-auto text-center space-y-4 mb-14" initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInVariants}>
          <div className="inline-flex items-center gap-2">
            <Badge variant="outline" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider gap-1.5 border-primary/20 bg-secondary/50 text-secondary-foreground">
              <MapPin className="size-3.5 text-accent" />
              Clayton County Headquarters
            </Badge>
          </div>
          
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight">
            Local Jonesboro, GA Emergency Plumbing Coverage
          </h2>
          
          <p className="text-base sm:text-lg text-muted-foreground font-default leading-relaxed">
            Based directly in Jonesboro, our mobile emergency plumbing units are stationed locally to reach your home or business fast when sudden leaks, clogs, or sewer backups happen.
          </p>
        </motion.div>

        {/* Dispatch Highlights Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-12">
          {dispatchGuarantees.map((item, i) => {
          const IconComponent = item.icon;
          return <div key={item.label} data-index={i} className="flex items-start gap-4 p-4 rounded-lg bg-card border border-border shadow-xs">
                <div className="p-2.5 rounded-md bg-primary/10 text-primary shrink-0">
                  <IconComponent className="size-5" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm sm:text-base text-card-foreground">
                    {item.label}
                  </h3>
                  <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                    {item.subtext}
                  </p>
                </div>
              </div>;
        })}
        </div>

        {/* Main 2-Column Content Section */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start mb-14">
          
          {/* Left Column: Neighborhoods & Service Boundaries */}
          <div className="lg:col-span-7 space-y-6">
            <Card className="border-border shadow-sm">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <CardTitle className="text-xl sm:text-2xl font-bold font-default text-card-foreground flex items-center gap-2">
                      <Navigation className="size-5 text-accent" />
                      Jonesboro Service Zones
                    </CardTitle>
                    <CardDescription className="text-muted-foreground mt-1">
                      Priority dispatch available 24 hours a day across all Jonesboro neighborhoods.
                    </CardDescription>
                  </div>
                  <Badge variant="secondary" className="font-mono text-xs">
                    ZIP: 30236 & 30238
                  </Badge>
                </div>
              </CardHeader>
              <Separator />
              <CardContent className="pt-6 space-y-3">
                {coverageAreas.map((area, i) => <div key={area.name} data-index={i} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 rounded-md bg-background border border-border hover:border-primary/40 transition-colors gap-2">
                    <div className="flex items-start gap-2.5">
                      <CheckCircle2 className="size-4 text-accent shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-sm text-foreground">{area.name}</p>
                        <p className="text-xs text-muted-foreground">{area.note}</p>
                      </div>
                    </div>
                    <span className="text-xs font-mono font-medium px-2 py-0.5 rounded bg-muted text-muted-foreground self-start sm:self-center">
                      {area.zip}
                    </span>
                  </div>)}
              </CardContent>
            </Card>

            {/* Localized Info Callout */}
            <div className="p-5 rounded-lg bg-secondary/40 border border-secondary-foreground/10 text-secondary-foreground flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              <div>
                <h4 className="font-bold text-sm sm:text-base">Need Service Outside Core Jonesboro?</h4>
                <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                  We also cover Lovejoy, Riverdale, Morrow, Stockbridge, and greater Clayton County.
                </p>
              </div>
              <Button asChild variant="outline" size="sm" className="shrink-0">
                <Link to="/service-areas/">
                  View All Areas
                  <ArrowRight className="size-3.5" />
                </Link>
              </Button>
            </div>
          </div>

          {/* Right Column: Local Response Capabilities & Map/Imagery */}
          <div className="lg:col-span-5 space-y-6">
            <Card className="border-border shadow-sm overflow-hidden">
              <div className="relative aspect-video w-full bg-muted overflow-hidden">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/jonesboro_service_area_map.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/jonesboro_service_area_map.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/jonesboro_service_area_map.png-wvc-sizes" alt="Jonesboro Georgia emergency plumbing mobile response service area" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                <div className="absolute top-3 left-3 bg-background/90 backdrop-blur-xs px-2.5 py-1 rounded-md text-xs font-medium text-foreground border border-border flex items-center gap-1.5 shadow-xs">
                  <span className="size-2 rounded-full bg-emerald-500 animate-pulse" />
                  Local Techs On Duty Now
                </div>
              </div>

              <CardHeader className="pb-3 pt-5">
                <CardTitle className="text-lg font-bold font-default">
                  Common Emergency Calls in Jonesboro
                </CardTitle>
                <CardDescription>
                  Our fully stocked service vans handle urgent plumbing emergencies on location.
                </CardDescription>
              </CardHeader>
              
              <CardContent className="space-y-3 pb-6">
                {serviceHighlights.map((srv, i) => <div key={srv.title} data-index={i} className="space-y-0.5">
                    <p className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                      <span className="size-1.5 rounded-full bg-primary" />
                      {srv.title}
                    </p>
                    <p className="text-xs text-muted-foreground pl-3">
                      {srv.description}
                    </p>
                  </div>)}
              </CardContent>
            </Card>

            {/* Quick Emergency Action Box */}
            <div className="rounded-xl bg-primary text-primary-foreground p-6 shadow-md relative overflow-hidden">
              <div className="relative z-10 space-y-4">
                <div className="space-y-1">
                  <Badge variant="secondary" className="text-secondary-foreground font-semibold text-xs mb-1">
                    Emergency Dispatch
                  </Badge>
                  <h3 className="text-xl font-bold font-default tracking-tight">
                    Plumbing Crisis in Jonesboro?
                  </h3>
                  <p className="text-xs sm:text-sm text-primary-foreground/85 leading-relaxed">
                    Do not wait for water damage to get worse. Speak directly with a local Jonesboro technician immediately.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-1">
                  <Button asChild size="lg" className="w-full sm:w-auto bg-accent text-accent-foreground hover:bg-accent/90 font-semibold shadow-xs">
                    <a href="tel:4708479076" className="inline-flex items-center justify-center gap-2">
                      <PhoneCall className="size-4" />
                      Call (470) 847-9076
                    </a>
                  </Button>

                  <Button asChild variant="outline" size="lg" className="w-full sm:w-auto bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/20">
                    <Link to="/contact/">
                      Request Dispatch
                    </Link>
                  </Button>
                </div>
              </div>
            </div>

          </div>

        </div>

      </div>
    </section>;
}